export const patientSummaryLoadingSteps = [
  "Fetching data of this patient",
  "Fetching lab reports",
  "Fetching previous prescriptions",
  "Fetching IPD summary",
  "Generating patient summary"
];

export const dischargeSummaryLoadingSteps = [
  "Fetching data of this patient",
  "Fetching admission details",
  "Fetching treatment notes",
  "Generating discharge summary"
];

const cleanSummaryText = (value) =>
  typeof value === "string"
    ? value.replace(/\r\n/g, "\n").trim()
    : "";

const formatSummaryBlocks = (blocks) => {
  if (!Array.isArray(blocks) || !blocks.length) {
    return "";
  }

  return blocks
    .map((block) => {
      const title = typeof block?.paragraph_title === "string"
        ? block.paragraph_title.trim()
        : "";
      const sentences = Array.isArray(block?.sentences)
        ? block.sentences
            .map((sentence) =>
              typeof sentence?.text === "string" ? sentence.text.trim() : ""
            )
            .filter(Boolean)
        : [];

      if (!title && !sentences.length) {
        return "";
      }

      if (title && sentences.length) {
        return `${title}\n${sentences.join(" ")}`;
      }

      return title || sentences.join(" ");
    })
    .filter(Boolean)
    .join("\n\n");
};

export const extractSummaryText = (payload) => {
  if (!payload) return "";

  if (typeof payload === "string") {
    return cleanSummaryText(payload);
  }

  const summaryBlocks =
    payload.summary_blocks ??
    payload.data?.summary_blocks;

  const formattedBlocks = formatSummaryBlocks(summaryBlocks);
  if (formattedBlocks) {
    return formattedBlocks;
  }

  const candidates = [
    payload.summary,
    payload.patient_summary,
    payload.discharge_summary,
    payload.treatment_summary,
    payload.content,
    payload.text,
    payload.output,
    payload.result,
    payload.message,
    payload.data?.summary,
    payload.data?.patient_summary,
    payload.data?.discharge_summary,
    payload.data?.treatment_summary,
    payload.data?.content,
    payload.data?.text,
    payload.data?.output,
    payload.data?.result,
    payload.data?.message
  ];

  const matched = candidates.find(
    (value) => typeof value === "string" && value.trim()
  );

  return matched ? cleanSummaryText(matched) : "";
};

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Summary reveal cancelled", "AbortError"));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException("Summary reveal cancelled", "AbortError"));
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });

const revealWords = async ({ text, onUpdate, signal, wordDelayMs = 28 }) => {
  const tokens = String(text || "").split(/(\s+)/);
  let displayed = "";

  for (const token of tokens) {
    displayed += token;
    onUpdate(displayed);
    if (token.trim()) {
      await wait(wordDelayMs, signal);
    }
  }

  return displayed;
};

const getSummaryBlocks = (payload) =>
  payload?.summary_blocks ?? payload?.data?.summary_blocks;

export const getSummaryBlocksFromPayload = getSummaryBlocks;

/**
 * Reveals HTTP summary payload progressively on the client.
 * Uses sentence-by-sentence pacing for summary_blocks, word-by-word otherwise.
 */
export const playSummaryRevealAnimation = async ({
  payload,
  onUpdate,
  signal,
  sentenceDelayMs = 380,
  titleDelayMs = 240,
  wordDelayMs = 28
}) => {
  const blocks = getSummaryBlocks(payload);

  if (Array.isArray(blocks) && blocks.length) {
    let displayed = "";

    for (const block of blocks) {
      const title = typeof block?.paragraph_title === "string"
        ? block.paragraph_title.trim()
        : "";
      const sentences = Array.isArray(block?.sentences) ? block.sentences : [];

      if (title) {
        if (displayed) {
          displayed += "\n\n";
          onUpdate(displayed);
          await wait(titleDelayMs, signal);
        }

        displayed += title;
        onUpdate(displayed);
        await wait(titleDelayMs, signal);
      }

      for (const sentence of sentences) {
        const text = typeof sentence?.text === "string" ? sentence.text.trim() : "";
        if (!text) continue;

        if (displayed && !displayed.endsWith("\n")) {
          displayed += "\n";
          onUpdate(displayed);
        }

        const tokens = text.split(/(\s+)/);

        for (const token of tokens) {
          displayed += token;
          onUpdate(displayed);
          if (token.trim()) {
            await wait(wordDelayMs, signal);
          }
        }

        await wait(sentenceDelayMs, signal);
      }
    }

    return displayed.trim();
  }

  const fullText = extractSummaryText(payload);
  if (!fullText) return "";

  return revealWords({ text: fullText, onUpdate, signal, wordDelayMs });
};

export const extractSummaryErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (data && typeof data === "object") {
    const detail = data.detail ?? data.error ?? data.message;
    if (typeof detail === "string" && detail.trim()) {
      return detail.trim();
    }
    if (Array.isArray(detail) && detail.length) {
      return detail.join(", ");
    }
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};
